import { EccoClubMemberDiscount } from "@components/commerce/product/right-panel/buy-card/ecco-club-member-discount/EccoClubMemberDiscount";
import { getDiscountToDisplay } from "@components/commerce/helpers";
import { NextLink } from "@components/ui/next-link/NextLink";
import { getPDPSlug } from "@lib/utils/getPDPSlug";
import type { EccoClubBadge, SaleBadge } from "@lib/wrappers/algolia/types";
import { Text } from "@ecco/ui/text";
import { IconButton } from "@ecco/ui/icon-button";
import { Box } from "@ecco/ui/box";
import type { FlexProps } from "@ecco/ui/flex";
import { Flex } from "@ecco/ui/flex";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { useBreakpoint } from "@ecco/ui/use-breakpoint";
import { useDisclosure } from "@ecco/ui/use-disclosure";
import { useInView } from "@ecco/ui/use-in-view";
import type { SwiperClass } from "@ecco/ui/swiper-exports";
import type { FC, MouseEventHandler } from "react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProductImages } from "./product-images/ProductImages";
import { ProductTilePrice } from "./product-tile-price/ProductTilePrice";
import { LinkStyles, ProductTileTheme } from "./ProductTile.styles";
import { eccoClubVerticalGradient } from "@ecco/ui/tokens/colors";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { CommonCMS } from "@lib/constants/contentful";

export interface ProductTileProps extends FlexProps {
    theme?: string;
    hasEccoClub: boolean;
    productName: string;
    articleNumber: string;
    colorCode: string;
    image: string;
    images?: string[];
    imageLabel?: string;
    variants?: Variant[];
    productColors: {
        colorCode: string;
        customBadges: string[];
        discountPercentage: number;
        hasDiscount: boolean;
        image: string;
        objectID: string;
        priceWithoutDiscount: Price;
        priceWithDiscount: Price;
        altTagPattern?: string;
        eaBadge?: SaleBadge;
        hasStock?: boolean;
        imageLabel?: string;
        lowestPrice?: Price;
        discountPercentageLowestPrice?: number;
    }[];
    objectID: string;
    categories?: string[];
    turnOffHover?: boolean;
    altTagPattern?: string;
    handleTileClick?: MouseEventHandler<HTMLAnchorElement>;
    hasDiscount?: boolean;
    eaBadge?: SaleBadge;
    hasStock?: boolean;
    locale: string;
    priceWithoutDiscount: Price;
    priceWithDiscount: Price;
    discountPercentage: number;
    colorLabel: string;
    tagComponent?: React.ReactElement<any>;
    isAddingToWishlist?: boolean;
    onAddToWishlist?: (product: Product) => Promise<void>;
    descriptiveHeading?: string;
    onInViewPort?: (inView: boolean, item: any) => any;
    eccoClubBadge?: EccoClubBadge;
    customBadges?: string[];
    hasCompactBadgesOnDesktop?: boolean;
    lowestPrice?: Price;
    discountPercentageLowestPrice?: number;
}

type Price = { centAmount: number; currencyCode: string; fractionDigits: number };
export type Variant = { size: string; availableQuantity: number; sku: string };

export type Product = Pick<
    ProductTileProps,
    | "priceWithDiscount"
    | "priceWithoutDiscount"
    | "image"
    | "imageLabel"
    | "variants"
    | "objectID"
    | "altTagPattern"
    | "colorCode"
    | "discountPercentage"
    | "hasDiscount"
    | "eaBadge"
    | "hasStock"
    | "lowestPrice"
    | "discountPercentageLowestPrice"
    | "customBadges"
>;

export const ProductTile: FC<ProductTileProps> = ({
    hasEccoClub = false,
    productName,
    articleNumber,
    colorCode,
    categories,
    image,
    images,
    imageLabel,
    variants,
    productColors,
    objectID,
    turnOffHover = false,
    altTagPattern,
    handleTileClick,
    hasDiscount = false,
    eaBadge,
    hasStock,
    locale,
    priceWithoutDiscount,
    priceWithDiscount,
    discountPercentage,
    colorLabel,
    isAddingToWishlist,
    onAddToWishlist,
    descriptiveHeading,
    onInViewPort,
    eccoClubBadge,
    customBadges,
    hasCompactBadgesOnDesktop,
    lowestPrice,
    discountPercentageLowestPrice,
    theme,
    ...rest
}) => {
    const { isOpen: isExpanded, onOpen, onClose } = useDisclosure();
    const md = useBreakpoint("md");
    const isMobile = md === false;
    const isLg = useBreakpoint("lg");

    const swiperRef = useRef<SwiperClass | null>(null);

    const initialProduct = useMemo<Product>(() => {
        return {
            priceWithDiscount,
            priceWithoutDiscount,
            image,
            imageLabel,
            variants,
            objectID,
            altTagPattern,
            colorCode,
            discountPercentage,
            hasDiscount,
            eaBadge,
            hasStock,
            customBadges,
            lowestPrice,
            discountPercentageLowestPrice,
        };
    }, [
        colorCode,
        priceWithoutDiscount,
        objectID,
        priceWithDiscount,
        image,
        imageLabel,
        altTagPattern,
        variants,
        hasDiscount,
        eaBadge,
        hasStock,
        discountPercentage,
        customBadges,
        lowestPrice,
        discountPercentageLowestPrice,
    ]);

    const [displayedProduct, setDisplayedProduct] = useState<Product>(initialProduct);

    useEffect(() => {
        setDisplayedProduct(initialProduct);
    }, [initialProduct]);

    const handleOnOpen = () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !turnOffHover && onOpen();
    };

    const handleOnClose = () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !turnOffHover && onClose();
    };

    const { slug: productSlug } = useMemo(
        () => getPDPSlug(productName, articleNumber, displayedProduct.colorCode, locale),
        [productName, articleNumber, displayedProduct.colorCode, locale]
    );

    const addToWishlist = useCallback(async () => {
        if (onAddToWishlist) {
            await onAddToWishlist(initialProduct);
        }
    }, [initialProduct]);

    const { ref } = useInView({
        threshold: 0.5,
        onChange: (inView) => {
            if (onInViewPort) {
                onInViewPort(inView, objectID);
            }
        },
    });

    // We need to check if the next item is a button, if not we close the modal (that means is a link thus is another product tile)
    const handleOnBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        if (e.relatedTarget?.classList.contains("chakra-link")) {
            onClose();
        }
    };

    const isDiscounted = displayedProduct?.discountPercentage > 0;
    const discountToDisplay = getDiscountToDisplay({
        isDiscounted,
        currentPrice: displayedProduct?.priceWithDiscount?.centAmount,
        previousLowestPrice: displayedProduct?.lowestPrice?.centAmount,
        recommendedPrice: displayedProduct?.priceWithoutDiscount?.centAmount,
        discountPercentage: displayedProduct?.discountPercentage,
        discountPercentagePreviousLowest: displayedProduct?.discountPercentageLowestPrice,
    });

    const themeSurface = useMemo(() => ProductTileTheme[theme]?.surface, [theme]);
    const themeText = useMemo(() => ProductTileTheme[theme]?.text, [theme]);
    const themeTextGray = useMemo(() => ProductTileTheme[theme]?.textGray, [theme]);
    const { get: getMicrocopy } = useMicrocopy();

    const isPreviewingVariant = displayedProduct.objectID !== initialProduct.objectID;
    const hasCarousel = !isPreviewingVariant && !!images && images.length > 1;
    const showCarouselArrows = isLg && isExpanded && hasCarousel;

    return (
        <Box
            data-testid="ProductTile"
            h={["full"]}
            ref={ref}
            {...rest}
            onFocus={onOpen}
            position="relative"
            onBlur={handleOnBlur}
            {...themeSurface}
            display="flex"
            flexDirection="column"
        >
            <NextLink
                href={productSlug}
                onClick={handleTileClick}
                sx={eccoClubBadge ? { ...LinkStyles, h: "auto" } : LinkStyles}
                tabIndex={0}
            >
                <Box
                    display={["flex", "flex"]}
                    flexDirection={"column"}
                    gridTemplateRows={turnOffHover ? "70% 30%" : ["70% 0% 30%", "70% 15% 15%"]}
                    h={["auto", "auto"]}
                    backgroundColor={"white"}
                    aria-label={productName}
                    {...themeSurface}
                >
                    <ProductImages
                        setDisplayedProduct={setDisplayedProduct}
                        displayedProduct={displayedProduct}
                        initialProduct={initialProduct}
                        altTagPattern={altTagPattern}
                        productColors={productColors}
                        productName={productName}
                        isExpanded={isExpanded}
                        onOpen={handleOnOpen}
                        onClose={handleOnClose}
                        productSlug={productSlug}
                        discountPercentage={discountToDisplay}
                        customBadges={displayedProduct.customBadges}
                        eccoClubBadge={isPreviewingVariant ? undefined : eccoClubBadge}
                        hasCompactBadgesOnDesktop={hasCompactBadgesOnDesktop}
                        images={images}
                        onCarouselSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                    />
                    <Box
                        pt={2}
                        pl={[4, 4, 4, 6]}
                        transition="margin 150ms ease-in-out"
                        sx={{ containerType: "inline-size" }}
                    >
                        <Box as={"h2"} display="flex" flexDirection="column">
                            {hasEccoClub && (
                                <Text
                                    as={"span"}
                                    fontSize={"12px"}
                                    fontStyle={"normal"}
                                    fontWeight="700"
                                    background={eccoClubVerticalGradient}
                                    backgroundClip="text"
                                    color="transparent"
                                    mt={"20px"}
                                    mb={"8px"}
                                >
                                    {getMicrocopy(CommonCMS.shared, CommonCMS.eccoClubEarlyAccess)}
                                </Text>
                            )}
                            <Text
                                as={"span"}
                                fontWeight="bold"
                                className="product-name"
                                fontSize={["desktopBodyTextSmall", "desktopBodyTextNormal"]}
                                color="black"
                                {...themeText}
                            >
                                {productName}
                            </Text>
                            {!isExpanded && descriptiveHeading && (
                                <Text
                                    as={"span"}
                                    fontWeight="bold"
                                    color="gray.500"
                                    className="product-colors"
                                    fontSize={["desktopBodyTextSmall", "desktopBodyTextNormal"]}
                                    {...themeTextGray}
                                >
                                    {descriptiveHeading}
                                </Text>
                            )}
                        </Box>
                        {/* Colors */}
                        <Text
                            fontWeight="bold"
                            color="gray.500"
                            className="product-colors"
                            fontSize={["desktopBodyTextSmall", "desktopBodyTextNormal"]}
                            {...themeTextGray}
                        >
                            {!isExpanded ? colorLabel : null}
                        </Text>
                        <ProductTilePrice
                            price={{
                                currentPrice: displayedProduct?.priceWithDiscount,
                                recommendedPrice: displayedProduct?.priceWithoutDiscount,
                                previousLowestPrice: displayedProduct?.lowestPrice,
                                discountPercentage: displayedProduct?.discountPercentage ?? 0,
                                discountPercentagePreviousLowest:
                                    displayedProduct?.discountPercentageLowestPrice ?? 0,
                            }}
                            isExpanded={isExpanded}
                            theme={theme}
                            isDiscounted={discountToDisplay > 0}
                        />
                    </Box>
                </Box>
            </NextLink>
            {eccoClubBadge && (
                <Box
                    pl={[4, 4, 4, 6]}
                    flexGrow={1}
                    visibility={isPreviewingVariant ? "hidden" : undefined}
                    onFocus={(event) => event.stopPropagation()}
                >
                    <EccoClubMemberDiscount badge={eccoClubBadge} variant="plp" />
                </Box>
            )}
            {showCarouselArrows && (
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    zIndex="1"
                    pointerEvents="none"
                    aspectRatio="4/5"
                >
                    <Flex
                        position="absolute"
                        bottom={5}
                        right={6}
                        gap={1}
                        pointerEvents="auto"
                        onMouseEnter={onOpen}
                        onMouseLeave={onClose}
                    >
                        <IconButton
                            size="sm"
                            variant="tertiary"
                            aria-label="Previous image"
                            onClick={() => swiperRef.current?.slidePrev()}
                            bgColor="whiteAlpha.800"
                            border="1px solid black"
                            sx={{
                                "&:hover": { bgColor: "white" },
                            }}
                            icon={<ECCOIcon name="chevron-left" size="2xs" />}
                        />
                        <IconButton
                            size="sm"
                            variant="tertiary"
                            aria-label="Next image"
                            onClick={() => swiperRef.current?.slideNext()}
                            bgColor="whiteAlpha.800"
                            border="1px solid black"
                            sx={{
                                "&:hover": { bgColor: "white" },
                            }}
                            icon={<ECCOIcon name="chevron-right" size="2xs" />}
                        />
                    </Flex>
                </Box>
            )}
            {(isExpanded || isMobile) && (
                <Box onMouseEnter={onOpen} onMouseLeave={onClose}>
                    <Flex position="absolute" top={1} right={1} zIndex="1">
                        <Flex gap={[0, 4, 6]} justifyContent="flex-end">
                            <IconButton
                                tabIndex={0}
                                size={"sm"}
                                aria-label="Add to wishlist"
                                color={"black"}
                                variant="tertiary"
                                bgColor="transparent"
                                sx={{
                                    "&:hover": {
                                        bgColor: "transparent",
                                    },
                                }}
                                icon={
                                    <ECCOIcon
                                        name={isAddingToWishlist ? "wishlist-filled" : "wishlist"}
                                    />
                                }
                                onClick={addToWishlist}
                            />
                        </Flex>
                    </Flex>
                </Box>
            )}
        </Box>
    );
};
